import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, MapPin, Eye, ArrowLeft, Share2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/${slug}`)
      .then((r) => {
        setPost(r.data);
        document.title = `${r.data.title} | Krishna Property Blog`;
        const desc = r.data.excerpt || r.data.title;
        let meta = document.querySelector("meta[name='description']");
        if (meta) meta.setAttribute("content", desc);
      })
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
    return () => { document.title = "Krishna Property — Buy, Sell, Rent & Lease in Surat"; };
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: post.title, url }); } catch {}
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-16 text-slate-500">Loading article…</div>;
  if (!post) return (
    <div className="max-w-3xl mx-auto px-4 py-16 text-center">
      <p className="text-slate-500">Article not found.</p>
      <Link to="/blog" className="inline-block mt-4"><Button variant="outline">Back to Blog</Button></Link>
    </div>
  );

  return (
    <article className="max-w-3xl mx-auto px-4 py-10" data-testid="blog-post-page">
      <Link to="/blog" className="text-sm text-slate-500 hover:text-kp-teal flex items-center gap-1 mb-4"><ArrowLeft size={14}/> All Articles</Link>
      {post.cover_image && <img src={post.cover_image} alt={post.title} className="w-full h-72 object-cover rounded-2xl mb-6"/>}
      <div className="flex items-center gap-3 text-xs text-slate-500 mb-3">
        <span className="flex items-center gap-1"><Calendar size={12}/> {new Date(post.created_at).toLocaleDateString("en-IN", {day:"numeric", month:"long", year:"numeric"})}</span>
        {post.locality && <Link to={`/locality/${post.locality.toLowerCase().replace(/\s+/g, "-")}`} className="flex items-center gap-1 text-kp-maroon hover:underline"><MapPin size={12}/> {post.locality}</Link>}
        <span className="flex items-center gap-1"><Eye size={12}/> {post.views} reads</span>
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 leading-tight">{post.title}</h1>
      {post.excerpt && <p className="text-lg text-slate-600 mt-3 leading-relaxed">{post.excerpt}</p>}
      <div className="mt-4 flex items-center gap-2 pb-6 border-b border-slate-200">
        <span className="text-sm text-slate-500">By <b className="text-slate-800">{post.author}</b></span>
        <div className="ml-auto"><Button size="sm" variant="outline" onClick={share} className="rounded-full gap-1" data-testid="blog-share"><Share2 size={12}/> Share</Button></div>
      </div>

      <div className="prose prose-slate max-w-none mt-8 blog-content" dangerouslySetInnerHTML={{ __html: post.content_html || "" }}/>

      {post.tags?.length > 0 && (
        <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap gap-2">
          {post.tags.map((t) => <span key={t} className="text-xs bg-slate-100 text-slate-700 font-semibold px-3 py-1 rounded-full">#{t}</span>)}
        </div>
      )}

      <div className="mt-10 bg-kp-teal-light border border-kp-teal/20 rounded-2xl p-6 text-center">
        <div className="font-display text-xl font-semibold text-slate-900">Looking for property {post.locality ? `in ${post.locality}` : "in Surat"}?</div>
        <p className="text-sm text-slate-600 mt-1">Browse verified listings on Krishna Property.</p>
        <Link to={post.locality ? `/locality/${post.locality.toLowerCase().replace(/\s+/g, "-")}` : "/properties"} className="inline-block mt-3">
          <Button className="btn-primary rounded-full">Explore Properties</Button>
        </Link>
      </div>
    </article>
  );
}
